import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Zap, Sliders, Check, ChevronRight, FlaskConical } from 'lucide-react';
import AppleHeader from '../components/AppleHeader';
import { loadLastCrearAnuncioState, mergeLastCrearAnuncioState } from '../utils/crearAnuncioStorage';
import { supabase } from '../lib/supabase';
import { trackInteraction } from '../utils/trackInteraction';

const FASHION_LABEL = 'Moda e Indumentaria';

type ModoKey = 'rapido' | 'personalizado' | 'beta_ui';

export const SeleccionarModoAnuncio: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idsParam = params.get('ids') || '';
  const ids = useMemo(() => idsParam.split(',').map((s) => s.trim()).filter(Boolean), [idsParam]);

  const last = loadLastCrearAnuncioState();
  const rubro = last?.rubro || FASHION_LABEL;
  const isFashion = rubro === FASHION_LABEL;

  const [selectedMode, setSelectedMode] = useState<ModoKey | null>(null);
  const [isBetaTester, setIsBetaTester] = useState<boolean>(false);

  // Scroll to top on mount (iOS Safari fix)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { if (mounted) setIsBetaTester(false); return; }
        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();
        const idCompany = (profile as any)?.id_company as string | null;
        if (!idCompany) { if (mounted) setIsBetaTester(false); return; }
        const { data: company } = await supabase
          .from('company_profiles')
          .select('beta_tester')
          .eq('id_company', idCompany)
          .single();
        if (mounted) setIsBetaTester(((company as any)?.beta_tester === true));
      } catch {
        if (mounted) setIsBetaTester(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleSelect = (mode: ModoKey) => {
    setSelectedMode(mode);
  };

  const handleContinue = () => {
    if (!selectedMode) return;
    if (selectedMode === 'beta_ui') {
      const qp = new URLSearchParams();
      if (ids.length > 0) qp.set('ids', ids.join(','));
      navigate(`/redes-sociales/crear-anuncio/prueba-nueva-interfaz?${qp.toString()}`);
      return;
    }
    mergeLastCrearAnuncioState({ modo: selectedMode, selectedVariantIds: ids });
    const qp = new URLSearchParams();
    if (ids.length > 0) qp.set('ids', ids.join(','));
    qp.set('modo', selectedMode);
    try {
      if (selectedMode === 'rapido') {
        trackInteraction('modo_automatico');
      } else if (selectedMode === 'personalizado') {
        trackInteraction('modo_personalizado');
      }
    } catch {}
    if (selectedMode === 'rapido') {
      navigate(`/redes-sociales/crear-anuncio/seleccionar-objetivo?${qp.toString()}`);
    } else if (!isFashion) {
      qp.set('composicion', 'modelo');
      navigate(`/redes-sociales/crear-anuncio/seleccionar-formato?${qp.toString()}`);
    } else {
      navigate(`/redes-sociales/crear-anuncio/seleccionar-tipo-imagen?${qp.toString()}`);
    }
  };

  return (
    <div className="p-4 sm:p-8 sm:pb-0 max-w-4xl mx-auto min-h-[100dvh] md:min-h-screen bg-white sm:bg-transparent">
      <div className="sm:bg-white sm:rounded-2xl sm:shadow-sm p-0 sm:p-6">
        <AppleHeader title="Seleccioná el modo" />
        <div className="mt-4">
          <div className="w-full h-1 bg-gray-200 rounded-full">
            <div className="h-1 bg-blue-500 rounded-full" style={{ width: '25%' }} />
          </div>
        </div>

        {/* Modos */}
        <div className="mt-6 grid grid-cols-1 gap-3">
          {/* Modo automático */}
          <button
            type="button"
            onClick={() => handleSelect('rapido')}
            className={`w-full text-left border ${selectedMode === 'rapido' ? 'border-blue-500 ring-2 ring-blue-500' : 'border-gray-200'} rounded-2xl p-4 flex items-start gap-4 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors`}
          >
            <div className="shrink-0 bg-blue-50 p-2 rounded-md">
              <Zap className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900">Modo automático</h2>
                {selectedMode === 'rapido' && (
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white">
                    <Check className="w-4 h-4" />
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 mt-1">Creá imágenes de forma sencilla y automática. Ideal para obtener resultados en pocos pasos.</p>
            </div>
          </button>

          {/* Modo personalizado */}
          <button
            type="button"
            onClick={() => handleSelect('personalizado')}
            className={`w-full text-left border rounded-2xl p-4 flex items-start gap-4 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
              selectedMode === 'personalizado' ? 'border-blue-500 ring-2 ring-blue-500' : 'border-gray-200'
            } hover:bg-gray-50`}
          >
            <div className="shrink-0 bg-blue-50 p-2 rounded-md">
              <Sliders className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900">Modo personalizado</h2>
                {selectedMode === 'personalizado' && (
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white">
                    <Check className="w-4 h-4" />
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 mt-1">Elegí opciones más específicas para ajustar el anuncio a tu marca y objetivos.</p>
            </div>
          </button>

          {/* Modo beta: Prueba de nueva interfaz (solo beta testers) */}
          {isBetaTester && (
            <button
              type="button"
              onClick={() => setSelectedMode('beta_ui')}
              className={`w-full text-left border rounded-2xl p-4 flex items-start gap-4 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
                selectedMode === 'beta_ui' ? 'border-blue-500 ring-2 ring-blue-500' : 'border-gray-200'
              } hover:bg-gray-50`}
            >
              <div className="shrink-0 bg-blue-50 p-2 rounded-md">
                <FlaskConical className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900">Prueba de nueva interfaz</h2>
                  {selectedMode === 'beta_ui' && (
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white">
                      <Check className="w-4 h-4" />
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">Gracias por ser parte de nuestro programa de pruebas para clientes estrategicos. Contanos que te parece esta nueva interfaz, con la que podríamos juntar y remplazar a los demás modos.</p>
              </div>
            </button>
          )}
        </div>

        {/* Footer actions (desktop) */}
        <div className="mt-6 hidden sm:flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
          >
            Volver
          </button>

          <button
            type="button"
            disabled={!selectedMode}
            className={`inline-flex items-center gap-2 px-4 h-10 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm ${
              !selectedMode
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            }`}
            onClick={handleContinue}
          >
            Continuar
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Mobile spacer for fixed bottom bar */}
      <div className="sm:hidden" style={{ height: 'calc(env(safe-area-inset-bottom, 0px) + 64px)' }} />

      {/* Mobile bottom bar */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-30">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
          >
            Volver
          </button>
          <button
            type="button"
            disabled={!selectedMode}
            className={`inline-flex items-center gap-2 px-4 h-10 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm ${
              !selectedMode
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            }`}
            onClick={handleContinue}
          >
            Continuar
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SeleccionarModoAnuncio;
